/**
 * Generates a circular favicon from an image URL
 * Creates a 32x32px transparent canvas with circular cropped image
 */

// Clean image URL to prevent double prefixes
function cleanImageUrl(url: string): string {
  if (!url) return url;
  // Clean double prefixes
  let cleaned = url;
  cleaned = cleaned.replace(/\/backend\/backend\//g, '/backend/');
  cleaned = cleaned.replace(/\/public\/public\//g, '/public/');
  return cleaned;
}

// Simple image loader with CORS support
async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Allow cross-origin images for canvas
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    // Clean URL before setting src
    img.src = cleanImageUrl(src);
  });
}

/**
 * Generates a circular favicon from an image URL
 * @param imageUrl - Absolute URL of the image to use for favicon
 * @returns Data URL of the generated favicon (PNG format) or null on error
 */
export async function generateFaviconFromImage(imageUrl: string): Promise<string | null> {
  try {
    // Clean image URL before loading
    const cleanedImageUrl = cleanImageUrl(imageUrl);
    
    // Load the source image
    const sourceImage = await loadImage(cleanedImageUrl);
    
    // Favicon size (32x32px is standard)
    const faviconSize = 32;
    
    // Create canvas with transparent background
    const canvas = document.createElement('canvas');
    canvas.width = faviconSize;
    canvas.height = faviconSize;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('Canvas context not available');
      return null;
    }
    
    // Enable transparency
    ctx.clearRect(0, 0, faviconSize, faviconSize);
    
    // Calculate square crop dimensions (center crop)
    const sourceWidth = sourceImage.width;
    const sourceHeight = sourceImage.height;
    const sourceAspect = sourceWidth / sourceHeight;
    
    let cropSize: number;
    let sourceX = 0;
    let sourceY = 0;
    
    if (sourceAspect > 1) {
      // Source is wider than tall - crop width
      cropSize = sourceHeight;
      sourceX = (sourceWidth - cropSize) / 2;
    } else {
      // Source is taller than wide - crop height
      cropSize = sourceWidth;
      sourceY = (sourceHeight - cropSize) / 2;
    }
    
    // Create circular clipping path
    ctx.save();
    ctx.beginPath();
    ctx.arc(faviconSize / 2, faviconSize / 2, faviconSize / 2, 0, Math.PI * 2);
    ctx.clip();
    
    // Draw the cropped image onto the canvas
    ctx.drawImage(
      sourceImage,
      sourceX, sourceY, cropSize, cropSize, // Source: square crop from center
      0, 0, faviconSize, faviconSize // Destination: full canvas
    );
    
    ctx.restore();
    
    // Convert to data URL (PNG format for transparency support)
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating favicon from image:', error);
    return null;
  }
}



